/**
 * Filtering for the sounds browser.
 *
 * Pure, so the behaviour that matters can be tested without a DOM. The behaviour
 * that matters is mostly about *absence*: in Strudel a name that does not exist
 * plays silence rather than erroring, so a learner searching for a sound they read
 * about somewhere needs to be told it is not here — otherwise they conclude their
 * code is broken and go looking for a bug that does not exist.
 */

export interface SoundBank {
  name: string;
  variants: number;
  description: string | null;
}

export interface SoundsCatalogue {
  domain_slug: string;
  source: string;
  retrieved: string;
  banks: SoundBank[];
  unavailable: string[];
}

export interface FilteredSounds {
  /** Conventional drum abbreviations, shown first because they are the ones used most. */
  kit: SoundBank[];
  /** Everything else, alphabetical. */
  rest: SoundBank[];
  /** Names matching the query that exist in Strudel's docs but not in this pack. */
  missing: string[];
  total: number;
}

/**
 * Filter the catalogue for a query.
 *
 * Matching is a substring on the name, plus the description for the drum kit, so
 * searching "hat" finds `hh` — a beginner is far more likely to know the word than
 * the abbreviation.
 */
export function filterSounds(
  catalogue: SoundsCatalogue | null,
  query: string,
): FilteredSounds {
  if (!catalogue) return { kit: [], rest: [], missing: [], total: 0 };

  const needle = query.trim().toLowerCase();
  const matches = (bank: SoundBank) =>
    !needle ||
    bank.name.toLowerCase().includes(needle) ||
    (bank.description ?? "").toLowerCase().includes(needle);

  const hits = catalogue.banks.filter(matches);
  return {
    kit: hits.filter((b) => b.description !== null),
    rest: hits.filter((b) => b.description === null),
    missing: needle
      ? catalogue.unavailable.filter((name) => name.toLowerCase().includes(needle))
      : catalogue.unavailable,
    total: hits.length,
  };
}

/**
 * What to put in the editor when a learner clicks a sound.
 *
 * Inserting the bare name rather than a whole `sound("…")` call is deliberate: the
 * learner is usually adding to a pattern they are already writing, and wrapping it
 * for them would produce nonsense inside an existing string. It also leaves the
 * `sound(...)` call as something they type — which is the concept the graph tracks.
 */
export function insertAt(text: string, cursor: number, name: string): {
  text: string;
  cursor: number;
} {
  const safeCursor = Math.max(0, Math.min(cursor, text.length));
  const before = text.slice(0, safeCursor);
  const after = text.slice(safeCursor);

  // Mini-notation is whitespace-separated, so both sides need checking —
  // inserting into the middle of `sound("bd |sd")` without a trailing space
  // silently fuses two sounds into one nonexistent name, which then plays
  // silence. Not adding a space where one is not wanted matters too: a leading
  // space just inside an opening quote is a rest, not nothing.
  const needsLeadingSpace = /[\w"')\]>}]$/.test(before) && !/["'\s([<{]$/.test(before);
  const needsTrailingSpace = /^[\w([<{]/.test(after);

  const insertion = `${needsLeadingSpace ? " " : ""}${name}${needsTrailingSpace ? " " : ""}`;
  return {
    text: before + insertion + after,
    // Leave the caret after the name, before any space we added, so typing
    // continues where the learner expects.
    cursor: safeCursor + insertion.length - (needsTrailingSpace ? 1 : 0),
  };
}
